import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PrimaryButton, IconButton, ContextualMenu } from "@fluentui/react";
import { Spinner, SpinnerSize } from '@fluentui/react';
import AddComment from "components/AddComment";
import { getComments, fetchCommentById, removeComment } from "store/actions/commentActions";
import { fetchPresignedUrl } from "store/actions/storageActions";
import { environment } from "environments/environment";
import logger from "utils/logger";
import { getUserId } from 'utils/authUtils';

const GeneralComment = ({ closeDrawer, id = null, isCompleted = false }) => {
    const dispatch = useDispatch();
    const currentUser = getUserId();
    const { loading, comments } = useSelector((state) => state.comment);
    const [showAddComment, setShowAddComment] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [contextMenuTarget, setContextMenuTarget] = useState(null);
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [allComments, setAllComments] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const pageSize = 10;

    const handleRefresh = () => {
        if (id) {
            setPageIndex(0);
            setAllComments([]);
            setHasMore(true);
            dispatch(getComments({
                workItemId: id,
                sort: 'createdAt',
                order: 'desc',
                pageIndex: 0,
                pageSize : pageSize,
             }));
        }
    };

    // Initial load
    useEffect(() => {
        if (id) {
            setPageIndex(0);
            setAllComments([]);
            setHasMore(true);
            dispatch(getComments({
                workItemId: id,
                sort: 'createdAt',
                order: 'desc',
                pageIndex: 0,
                pageSize: pageSize,
            }));
        }
    }, [id, dispatch, pageSize]);

    // Accumulate comments when new data arrives
    useEffect(() => {
        if (comments && comments) {
            if (pageIndex === 0) {
                setAllComments(comments);
            } else {
                setAllComments(prev => [...prev, ...comments]);
            }

            setHasMore(comments.length === pageSize);
        }
    }, [comments, pageIndex, pageSize]);

    // Load more comments
    const loadMoreComments = useCallback(() => {
        if (!loading && hasMore && id) {
            const nextPage = pageIndex + 1;
            setPageIndex(nextPage);
            dispatch(getComments({
                workItemId: id,
                sort: 'createdAt',
                order: 'desc',
                pageIndex: nextPage,
                pageSize: pageSize,
            }));
        }
    }, [loading, hasMore, id, pageIndex, dispatch, pageSize]);

    // Infinite scroll handler
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        // Load more when scrolled to 80% of content
        if (scrollHeight - scrollTop <= clientHeight * 1.2) {
            loadMoreComments();
        }
    }, [loadMoreComments]);

    const handleCommentSuccess = () => {
        setShowAddComment(false);
        setEditingComment(null);
        setSelectedCommentId(null);
        handleRefresh();
    };

    const handleCommentCancel = () => {
        setShowAddComment(false);
        setEditingComment(null);
        setSelectedCommentId(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const handleMenuClick = (event, commentId) => {
        setContextMenuTarget(event.currentTarget);
        setSelectedCommentId(commentId);
    };

    const handleMenuDismiss = () => {
        setContextMenuTarget(null);
        setSelectedCommentId(null);
    };

    const handleEdit = async () => {
        try {
            const commentData = await dispatch(fetchCommentById(selectedCommentId));
            setEditingComment(commentData);
            setShowAddComment(true);
            handleMenuDismiss();
        } catch (error) {
            logger.error('Failed to fetch comment:', error);
            handleMenuDismiss();
        }
    };

    const handleDelete = async () => {
        setContextMenuTarget(null);
        await dispatch(removeComment(selectedCommentId));
        setSelectedCommentId(null);
        handleRefresh();
    };

    const handleFileOpen = async (attachment) => {

        logger.log('Attachment to open:', attachment);

        if (!attachment) return;

        try {
            const params = {
                bucketName: environment.storage.s3.mediaBucketName,
                key: attachment.key,
                httpVerb: 0, // GET operation for viewing/downloading
                contentType: 0
            };

            logger.log('Content Type:', attachment.type)

            const presignedUrlResponse = await dispatch(fetchPresignedUrl(params));

            const presignedUrl = presignedUrlResponse?.url ||
                                presignedUrlResponse?.data?.url ||
                                presignedUrlResponse?.data ||
                                presignedUrlResponse;

            if (presignedUrl && typeof presignedUrl === 'string') {

                const response = await fetch(presignedUrl);
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = attachment.name || 'download';
                link.click();

                window.URL.revokeObjectURL(blobUrl);
            } else {
                logger.error('Invalid presigned URL received');
            }
        } catch (error) {
            logger.error('Failed to get presigned URL:', error);
        }
    };

    const isCommentCreator = (comment) => {
        return comment.createById === currentUser;
    };

    const menuItems = [
        {
            key: 'edit',
            text: 'Edit',
            onClick: handleEdit
        },
        {
            key: 'delete',
            text: 'Delete',
            onClick: handleDelete
        }
    ];

    return (
        <div className="drawer-backdrop">
            <div className="drawer">
                <div className="drawer-header">
                    <div className="heading">
                        Comments
                    </div>
                    <button className="close-btn" onClick={closeDrawer}>&times;</button>
                </div>
                <div className="drawer-content">

                    {!showAddComment && !isCompleted && (
                        <div className="col-12">
                            <div className="d-flex justify-content-end comment-header-actions">
                                <PrimaryButton
                                    text="Add Comment"
                                    type="button"
                                    onClick={() => setShowAddComment(true)}
                                />
                            </div>
                        </div>
                    )}

                    {showAddComment && (
                        <AddComment
                            workItemId={id}
                            editingComment={editingComment}
                            onSuccess={handleCommentSuccess}
                            onCancel={handleCommentCancel}
                            submitButtonText="Comment"
                            placeholder="Enter your comment..."
                        />
                    )}

                    {!showAddComment && (
                        <div
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
                        >
                            {pageIndex === 0 && loading ? (
                                <div className="comments-loading">
                                    <Spinner size={SpinnerSize.medium} label="Loading comments..." />
                                </div>
                            ) : allComments.length > 0 ? (
                                <div>
                                {allComments.map((comment, index) => (
                                    <div key={comment.id || index} className="teams-card" style={{ margin: '15px 0px', position: 'relative' }}>
                                        {isCommentCreator(comment) && (
                                            <IconButton
                                                className='color-black'
                                                iconProps={{ iconName: "MoreVertical" }}
                                                title="More options"
                                                ariaLabel="More options"
                                                onClick={(e) => handleMenuClick(e, comment.id)}
                                                style={{ position: 'absolute', top: '10px', right: '10px' }}
                                            />
                                        )}
                                        <div className="form-detail">
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="label-text">Created By</div>
                                                    <div> {comment.createdByName}</div>
                                                </div>

                                                <div className="col-6">
                                                    <div className="label-text">Created On</div>
                                                    <div>{formatDate(comment.createdAt)}</div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="label-text">Comment</div>
                                                    <div className="word-break">{comment.comment ? comment.comment : "-"}</div>
                                                </div>
                                            </div>

                                            {comment.attachments && comment.attachments.length > 0 && (
                                                <div className="comment-attachments">
                                                    <div className="label-text">Attachment</div>
                                                    {comment.attachments.map((attachment, idx) => (
                                                        <div className="row" key={idx}>
                                                            <div className="col-12">
                                                                <div className="upload-text" style={{ marginBottom: '5px' }}>
                                                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                                                    <a
                                                                        href="#"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            handleFileOpen(attachment);
                                                                        }}
                                                                        className="attachment-link"
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {attachment.name}
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* Loading indicator for subsequent pages */}
                                {loading && pageIndex > 0 && (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spinner size={SpinnerSize.medium} label="Loading more comments..." />
                                    </div>
                                )}
                                </div>
                            ) : (
                                <div className="no-comments">
                                    <p>No comments yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {contextMenuTarget && (
                <ContextualMenu
                    items={menuItems}
                    target={contextMenuTarget}
                    onDismiss={handleMenuDismiss}
                />
            )}
        </div>
    );
};

export default GeneralComment;
